import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { cardPresentationLimits, type CardPresentationType, getInitialCardCount } from '@/utils/shouldCardBeShown';

import type { CryptoSymbol } from '../components/sections/TradeRequestV2/components/TradeDetailsStep/constants/cryptoConstants';
import {
  convertRequestTradeFormToApiFormat,
  SubmitTradeRequestResponse,
  tradeRequestApi,
} from '../services/tradeRequest';

export const tradeRequestFormTotalSteps = 4;

export interface TransactionInfo {
  cryptoType: '' | CryptoSymbol;
  purchaseAmount: string;
  isBusinessAccount: boolean;
  businessName?: string;
  registeredBusinessName?: string;
  receivingWalletAddress?: string;
  confirmWalletAddress?: string;
  memoId?: string;
  specialNotes?: string;
}

export interface ConfirmationInfo {
  confirmationChecked: boolean;
  riskAcknowledged: boolean;
}

interface NewTradeRequestFormState {
  currentStep: number;
  requestId: string | null;
  transactionInfo: TransactionInfo;
  confirmationInfo: ConfirmationInfo;
}

type CardCounts = {
  lastCompletedTradeCount: number;
  lastRejectedTradeCount: number;
  cardPresentedCounts: Record<CardPresentationType, number>;
};

interface CardState {
  cardCountsByUserId: Record<string, CardCounts>;
}

interface NewTradeRequestFormActions {
  setCurrentStep: (step: number) => void;
  updateTransactionInfo: (info: Partial<TransactionInfo>) => void;
  updateConfirmationInfo: (info: Partial<ConfirmationInfo>) => void;
  submitTradeRequest: () => Promise<SubmitTradeRequestResponse>;
  init: () => void;
  resetForm: () => void;
  resetDataOnly: () => void;
  setLastCompletedTradeCount: (userId: string, count: number) => void;
  setLastRejectedTradeCount: (userId: string, count: number) => void;
  setCardPresentedCount: (userId: string, cardType: CardPresentationType, count: number) => void;
  logout: () => void;
}

const initialState: NewTradeRequestFormState = {
  currentStep: 1,
  requestId: null,
  transactionInfo: {
    cryptoType: '',
    purchaseAmount: '',
    isBusinessAccount: false,
    businessName: undefined,
    registeredBusinessName: undefined,
    receivingWalletAddress: undefined,
    confirmWalletAddress: undefined,
    memoId: undefined,
    specialNotes: undefined,
  },
  confirmationInfo: {
    confirmationChecked: false,
    riskAcknowledged: false,
  },
};

const cardTypes = Object.keys(cardPresentationLimits) as CardPresentationType[];

const defaultCardCounts: CardCounts = {
  lastCompletedTradeCount: 0,
  lastRejectedTradeCount: 0,
  cardPresentedCounts: cardTypes.reduce(
    (acc, cardType) => ({
      ...acc,
      [cardType]: getInitialCardCount(cardType),
    }),
    {} as Record<CardPresentationType, number>,
  ),
};

export const useNewTradeRequestStore = create<
  NewTradeRequestFormState & CardState & NewTradeRequestFormActions,
  [['zustand/persist', { cardCountsByUserId: Record<string, CardCounts> }]]
>(
  persist(
    (set, get) => ({
      ...initialState,
      cardCountsByUserId: {},
      setLastCompletedTradeCount: (userId: string, count: number) =>
        set((state) => {
          const existing = state.cardCountsByUserId[userId] ?? defaultCardCounts;

          return {
            cardCountsByUserId: {
              ...state.cardCountsByUserId,
              [userId]: {
                ...existing,
                lastCompletedTradeCount: count,
                cardPresentedCounts: { ...existing.cardPresentedCounts },
              },
            },
          };
        }),
      setLastRejectedTradeCount: (userId: string, count: number) =>
        set((state) => {
          const existing = state.cardCountsByUserId[userId] ?? defaultCardCounts;

          return {
            cardCountsByUserId: {
              ...state.cardCountsByUserId,
              [userId]: {
                ...existing,
                lastRejectedTradeCount: count,
                cardPresentedCounts: { ...existing.cardPresentedCounts },
              },
            },
          };
        }),
      setCardPresentedCount: (userId: string, cardType: CardPresentationType, count: number) =>
        set((state) => {
          const existing = state.cardCountsByUserId[userId] ?? defaultCardCounts;

          return {
            cardCountsByUserId: {
              ...state.cardCountsByUserId,
              [userId]: {
                ...existing,
                cardPresentedCounts: {
                  ...existing.cardPresentedCounts,
                  [cardType]: count,
                },
              },
            },
          };
        }),

      setCurrentStep: (step) => {
        const clamped = Math.max(1, Math.min(step, tradeRequestFormTotalSteps));
        set({ currentStep: clamped });
        localStorage.setItem('trade-request-step', clamped.toString());
      },

      updateTransactionInfo: (info) => {
        set((state) => {
          const updated = { ...state.transactionInfo, ...info };
          try {
            localStorage.setItem('trade-request-transaction', JSON.stringify(updated));
          } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn('[TradeRequest] Failed to persist trade request info to localStorage:', err);
            }
          }
          return { transactionInfo: updated } as any;
        });
      },

      updateConfirmationInfo: (info) => {
        set((state) => {
          const updated = { ...state.confirmationInfo, ...info };
          try {
            localStorage.setItem('trade-request-confirmation', JSON.stringify(updated));
          } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn('[TradeRequest] Failed to persist trade request confirmation info to localStorage:', err);
            }
          }
          return { confirmationInfo: updated } as any;
        });
      },

      submitTradeRequest: async (): Promise<SubmitTradeRequestResponse> => {
        const { transactionInfo, confirmationInfo } = get();

        const formDataForApi = {
          transactionInfo,
          confirmationInfo,
        };

        try {
          const apiData = convertRequestTradeFormToApiFormat(formDataForApi);

          const response = await tradeRequestApi.submitTradeRequest(apiData);
          return response;
        } catch (error: any) {
          return {
            error: error.message || 'An unknown error occurred in the store action.',
          };
        }
      },

      init: () => {
        const savedStep = localStorage.getItem('trade-request-step');
        const savedConfirmation = localStorage.getItem('trade-request-confirmation');
        const savedTransaction = localStorage.getItem('trade-request-transaction');
        if (savedStep) {
          const parsed = parseInt(savedStep, 10);
          if (!Number.isNaN(parsed)) {
            // Clamp restored step in case storage contained a bogus number (e.g., 0 or 42)
            const clampedStep = Math.max(1, Math.min(parsed, tradeRequestFormTotalSteps));
            // If they previously hit the submitted screen, restart them on the last actionable step instead
            const normalizedStep =
              clampedStep === tradeRequestFormTotalSteps ? tradeRequestFormTotalSteps - 1 : clampedStep;
            set({ currentStep: normalizedStep });
          }
        }
        if (savedTransaction) {
          try {
            const parsed = JSON.parse(savedTransaction);
            set({ transactionInfo: { ...initialState.transactionInfo, ...parsed } });
          } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn('[TradeRequest] Failed to restore trade request info in localStorage:', err);
            }
          }
        }
        if (savedConfirmation) {
          try {
            const parsed = JSON.parse(savedConfirmation);
            set({ confirmationInfo: { ...initialState.confirmationInfo, ...parsed } });
          } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn('[TradeRequest] Failed to restore trade request confirmation info in localStorage:', err);
            }
          }
        }
      },

      resetDataOnly: () => {
        set({
          transactionInfo: { ...initialState.transactionInfo },
          confirmationInfo: { ...initialState.confirmationInfo },
        });
        try {
          localStorage.removeItem('trade-request-confirmation');
          localStorage.removeItem('trade-request-transaction');
          localStorage.removeItem('trade-request-step');
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[TradeRequest] Failed to update trade request form data in localStorage:', err);
          }
        }
      },

      resetForm: () => {
        get().resetDataOnly();

        set({ currentStep: 1 });
        try {
          localStorage.setItem('trade-request-step', '1');
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[TradeRequest] Failed to update trade request step in localStorage:', err);
          }
        }
      },

      // Logout method - reset form data but preserve banner storage
      logout: () => {
        const preservedCardCounts = get().cardCountsByUserId;
        set({ ...initialState, cardCountsByUserId: preservedCardCounts });
        try {
          localStorage.removeItem('trade-request-confirmation');
          localStorage.removeItem('trade-request-transaction');
          localStorage.removeItem('trade-request-step');
        } catch (error) {
          // non-fatal
        }
      },
    }),
    {
      name: 'banner-storage',
      partialize: (state) => ({
        cardCountsByUserId: state.cardCountsByUserId,
      }),
    },
  ),
);

useNewTradeRequestStore.getState().init();
